import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock, LogOut, Trophy, Crown, Gem, CheckCircle2 } from "lucide-react";
import { api, errMsg, fmtDate } from "../lib/api";
import { Input } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import { Switch } from "../components/ui/switch";
import { OtpVerify } from "../components/site/OtpVerify";
import { ShareButtons } from "../components/site/ShareButtons";
import { MilestoneCelebration } from "../components/site/MilestoneCelebration";

const ICON = { standard: Trophy, premium: Crown, exclusive: Gem };
const REDEMPTION = { awaiting_launch: "Unlocked · awaiting redemption at launch", pending_provisioning: "Unlocked · pending provisioning", provisioned: "Provisioned", redeemed: "Redeemed", superseded: "Superseded by higher tier", revoked: "Revoked" };

const ViewStats = ({ onDone }) => {
  const [email, setEmail] = useState("");
  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(false);
  const send = async (e) => {
    e.preventDefault(); setLoading(true);
    try { const { data } = await api.post("/auth/request-otp", { email: email.trim() }); setChallenge(data); toast.success(data.message); }
    catch (err) { toast.error(errMsg(err)); } finally { setLoading(false); }
  };
  return (
    <div className="max-w-md mx-auto card-surface lime-glow p-8" data-testid="view-stats-card">
      <Lock className="text-[#B7FF3C]" />
      <h1 className="mt-4 text-3xl font-black uppercase">View My Stats</h1>
      <p className="mt-2 mb-6 text-sm text-[#A3ADA3]">Your referral stats are private. Verify your registered email with a one-time code to continue.</p>
      {challenge ? <OtpVerify challenge={challenge} onVerified={onDone} /> : (
        <form onSubmit={send} className="space-y-4">
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Registered email" data-testid="stats-email-input" className="h-12 bg-[#080A08] border-[#2A362A]" />
          <button disabled={loading} data-testid="stats-send-otp-btn" className="w-full py-3.5 rounded-full bg-[#B7FF3C] text-[#080A08] font-bold flex justify-center hover:bg-[#c9ff6a]">{loading ? <Loader2 className="animate-spin" size={18} /> : "Send Verification Code"}</button>
          <p className="text-xs text-center text-[#7C8A7C]">Not registered yet? <Link to="/join" className="text-[#B7FF3C]">Join the waitlist</Link></p>
        </form>
      )}
    </div>
  );
};

const TierCard = ({ t }) => {
  const I = ICON[t.key];
  return (
    <div className={`card-surface p-6 ${t.unlocked ? "border-[#B7FF3C]/50 lime-glow" : ""}`} data-testid={`tier-card-${t.key}`}>
      <div className="flex items-center justify-between"><I className="text-[#B7FF3C]" size={22} />
        {t.unlocked ? <span className="flex items-center gap-1 text-xs font-mono text-[#B7FF3C]" data-testid={`tier-${t.key}-unlocked`}><CheckCircle2 size={14} />UNLOCKED</span>
          : <span className="text-xs font-mono text-[#7C8A7C]">{t.remaining} TO GO</span>}
      </div>
      <p className="mt-4 font-display text-xl font-bold uppercase">{t.name} — {t.threshold} referrals</p>
      <p className="mt-1 text-3xl font-black font-display" data-testid={`tier-${t.key}-progress`}>{t.progress}/{t.threshold}</p>
      <Progress value={(t.progress / t.threshold) * 100} className="mt-3 h-2 bg-[#1E281E] [&>div]:bg-[#B7FF3C]" />
      <p className="mt-3 text-sm text-[#A3ADA3]">Reward: {t.reward}</p>
    </div>
  );
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");
  const load = useCallback(async () => {
    try { const r = await api.get("/me/dashboard"); setData(r.data); setState("ready"); }
    catch { setState("auth"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const logout = async () => { await api.post("/auth/logout"); setData(null); setState("auth"); };
  const toggleConsent = async (v) => {
    try { await api.post("/email-preferences", { marketing_consent: v }); setData({ ...data, marketing_consent: v }); toast.success(v ? "Subscribed to product updates" : "Unsubscribed from promotional emails"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (state === "loading") return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin text-[#B7FF3C]" /></div>;
  if (state === "auth") return <div className="relative py-20 px-5 min-h-[80vh]"><div className="absolute inset-0 grid-bg opacity-40" /><div className="relative"><ViewStats onDone={load} /></div></div>;

  return (
    <div className="max-w-6xl mx-auto px-5 lg:px-8 py-14 space-y-8" data-testid="user-dashboard">
      <MilestoneCelebration memberNo={data.member_no} tiers={data.tiers} />
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">Private dashboard · Member #{data.member_no}</p>
          <h1 className="mt-2 text-4xl sm:text-5xl font-black uppercase" data-testid="dashboard-welcome">Welcome, {data.first_name}</h1>
          <p className="mt-2 text-[#A3ADA3] text-sm">Waitlist status: <span className="text-[#B7FF3C]" data-testid="dashboard-status">{data.status === "verified" ? "Verified — on the waitlist" : "Pending verification"}</span> · Joined {fmtDate(data.registered_at)}</p>
        </div>
        <button onClick={logout} data-testid="dashboard-logout-btn" className="flex items-center gap-2 text-sm text-[#A3ADA3] hover:text-white"><LogOut size={16} />Sign out</button>
      </div>

      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-5">
        <div className="card-surface p-7 space-y-5">
          <p className="eyebrow">Your referral link</p>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <code className="flex-1 font-mono text-sm bg-[#080A08] border border-[#2A362A] rounded-lg px-4 py-3 break-all" data-testid="referral-link">{data.referral_link}</code>
            <span className="font-mono text-sm text-[#7C8A7C]">Code: <span className="text-white" data-testid="referral-code">{data.referral_code}</span></span>
          </div>
          <ShareButtons link={data.referral_link} />
        </div>
        <div className="card-surface p-7">
          <p className="eyebrow">Eligible referrals</p>
          <p className="mt-2 font-display text-7xl font-bold leading-none text-white tabular-nums" data-testid="eligible-referral-count">{data.eligible_referrals}</p>
          <p className="text-sm text-[#A3ADA3]">You have {data.eligible_referrals} eligible referrals.{data.pending_referrals > 0 && ` ${data.pending_referrals} pending verification or review.`}</p>
          <p className="mt-4 text-sm">Current reward tier: <span className="font-semibold text-[#B7FF3C]" data-testid="current-tier">{data.current_tier_label}</span></p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-5">{data.tiers.map((t) => <TierCard key={t.key} t={t} />)}</div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="card-surface p-7" data-testid="reward-history">
          <p className="eyebrow mb-4">Reward history</p>
          {data.rewards.length === 0 ? <p className="text-sm text-[#7C8A7C]">No rewards unlocked yet. Reach {data.tiers[0].threshold} eligible referrals to unlock Standard.</p> :
            data.rewards.map((r) => (
              <div key={r.id} className="flex justify-between gap-3 py-3 border-b border-[#1E281E] last:border-0 text-sm">
                <span className="font-semibold capitalize">{r.tier}</span>
                <span className="text-[#A3ADA3] text-right">{REDEMPTION[r.redemption_status] || r.redemption_status}<br /><span className="text-xs text-[#5E6B5E]">{fmtDate(r.unlocked_at)}</span></span>
              </div>
            ))}
          {!data.subscriptions_live && <p className="mt-4 text-xs text-[#7C8A7C]">Subscriptions are not live yet. Unlocked rewards are held for redemption at launch.</p>}
        </div>
        <div className="card-surface p-7" data-testid="referral-activity">
          <p className="eyebrow mb-4">Recent referral activity</p>
          {data.recent_referrals.length === 0 ? <p className="text-sm text-[#7C8A7C]">No referrals yet. Share your link to get started.</p> :
            data.recent_referrals.slice(0, 10).map((r, i) => (
              <div key={i} className="flex justify-between py-2.5 border-b border-[#1E281E] last:border-0 text-sm">
                <span className="text-[#A3ADA3]">Referral · {fmtDate(r.date)}</span>
                <span className={r.status === "eligible" ? "text-[#B7FF3C]" : "text-[#7C8A7C]"}>{r.status === "eligible" ? "Eligible" : r.status === "pending" ? "Pending" : "Not eligible"}</span>
              </div>
            ))}
          <p className="mt-3 text-xs text-[#5E6B5E]">Invitee details are kept private.</p>
        </div>
      </div>

      <div className="card-surface p-7 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div><p className="font-semibold">Promotional emails</p><p className="text-sm text-[#A3ADA3]">Product news, launch announcements and offers. Essential account emails are always sent.</p></div>
        <Switch checked={data.marketing_consent} onCheckedChange={toggleConsent} data-testid="marketing-consent-switch" className="data-[state=checked]:bg-[#B7FF3C]" />
      </div>
    </div>
  );
}
